/**
 * The nav rail's width, remembered between visits.
 *
 * Bounds are deliberate rather than cosmetic: below the minimum the longest
 * department name wraps to three lines, and above the maximum the rail starts
 * taking space from the documents it exists to navigate to.
 */
export const RAIL_MIN = 200
export const RAIL_MAX = 420
export const RAIL_DEFAULT = 232

/** Folded up: wide enough for the icons, which stay navigable. */
export const RAIL_COLLAPSED = 64

const KEY = 'aeys.vco.rail-width'
const COLLAPSED_KEY = 'aeys.vco.rail-collapsed'

export const clampRailWidth = (px: number) =>
  Math.min(RAIL_MAX, Math.max(RAIL_MIN, Math.round(px)))

/**
 * Falls back to the default for anything unusable — a missing key, a value
 * written by an older build, or a number outside the current bounds. Storage
 * itself can throw (Safari private browsing denies access outright), and a
 * preference is never worth failing the shell over.
 */
export function readRailWidth(): number {
  try {
    const raw = localStorage.getItem(KEY)
    // Number('') is 0 — finite, and so it would clamp to the minimum width
    // rather than reading as "no preference recorded".
    if (raw === null || raw.trim() === '') return RAIL_DEFAULT
    const parsed = Number(raw)
    if (!Number.isFinite(parsed)) return RAIL_DEFAULT
    return clampRailWidth(parsed)
  } catch {
    return RAIL_DEFAULT
  }
}

export function saveRailWidth(px: number): void {
  try {
    localStorage.setItem(KEY, String(clampRailWidth(px)))
  } catch {
    // A rail that forgets its width is a smaller problem than one that crashes.
  }
}

/**
 * Stored separately from the width, so folding the rail up and opening it again
 * returns it to the width it had rather than the default.
 */
export function readRailCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true'
  } catch {
    return false
  }
}

export function saveRailCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, String(collapsed))
  } catch {
    // As above — a forgotten preference, not a broken shell.
  }
}
